/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, FormText, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getSeries } from 'app/entities/series/series.reducer';
import { createEntity, getEntity, reset, updateEntity } from './instance.reducer';

export const InstanceUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const series = useAppSelector(state => state.series.entities);
  const instanceEntity = useAppSelector(state => state.instance.entity);
  const loading = useAppSelector(state => state.instance.loading);
  const updating = useAppSelector(state => state.instance.updating);
  const updateSuccess = useAppSelector(state => state.instance.updateSuccess);

  const handleClose = () => {
    navigate(`/instance${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getSeries({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.instanceNumber !== undefined && typeof values.instanceNumber !== 'number') {
      values.instanceNumber = Number(values.instanceNumber);
    }
    values.acquisitionDateTime = convertDateTimeToServer(values.acquisitionDateTime);
    if (values.pixelSpacing !== undefined && typeof values.pixelSpacing !== 'number') {
      values.pixelSpacing = Number(values.pixelSpacing);
    }
    if (values.sliceThickness !== undefined && typeof values.sliceThickness !== 'number') {
      values.sliceThickness = Number(values.sliceThickness);
    }
    if (values.sliceLocation !== undefined && typeof values.sliceLocation !== 'number') {
      values.sliceLocation = Number(values.sliceLocation);
    }
    if (values.xrayTubeCurrent !== undefined && typeof values.xrayTubeCurrent !== 'number') {
      values.xrayTubeCurrent = Number(values.xrayTubeCurrent);
    }
    if (values.exposureTime !== undefined && typeof values.exposureTime !== 'number') {
      values.exposureTime = Number(values.exposureTime);
    }
    values.contentDateTime = convertDateTimeToServer(values.contentDateTime);
    if (values.columns !== undefined && typeof values.columns !== 'number') {
      values.columns = Number(values.columns);
    }
    if (values.rows !== undefined && typeof values.rows !== 'number') {
      values.rows = Number(values.rows);
    }
    if (values.photometricInterpretation !== undefined && typeof values.photometricInterpretation !== 'number') {
      values.photometricInterpretation = Number(values.photometricInterpretation);
    }
    if (values.bitsAllocated !== undefined && typeof values.bitsAllocated !== 'number') {
      values.bitsAllocated = Number(values.bitsAllocated);
    }
    if (values.bitsStored !== undefined && typeof values.bitsStored !== 'number') {
      values.bitsStored = Number(values.bitsStored);
    }
    if (values.pixelRepresentation !== undefined && typeof values.pixelRepresentation !== 'number') {
      values.pixelRepresentation = Number(values.pixelRepresentation);
    }
    if (values.highBit !== undefined && typeof values.highBit !== 'number') {
      values.highBit = Number(values.highBit);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampentry !== undefined && typeof values.timestampentry !== 'number') {
      values.timestampentry = Number(values.timestampentry);
    }
    if (values.lastupdated !== undefined && typeof values.lastupdated !== 'number') {
      values.lastupdated = Number(values.lastupdated);
    }
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }

    const entity = {
      ...instanceEntity,
      ...values,
      series: series.find(it => it.id.toString() === values.series?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          acquisitionDateTime: displayDefaultDateTime(),
          contentDateTime: displayDefaultDateTime(),
        }
      : {
          ...instanceEntity,
          acquisitionDateTime: convertDateTimeFromServer(instanceEntity.acquisitionDateTime),
          contentDateTime: convertDateTimeFromServer(instanceEntity.contentDateTime),
          series: instanceEntity?.series?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.instance.home.createOrEditLabel" data-cy="InstanceCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.instance.home.createOrEditLabel">Create or edit a Instance</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="instance-id"
                  label={translate('tinyMedicRestApp.instance.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.sopInstanceUid')}
                id="instance-sopInstanceUid"
                name="sopInstanceUid"
                data-cy="sopInstanceUid"
                type="text"
                validate={{
                  maxLength: { value: 100, message: translate('entity.validation.maxlength', { max: 100 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.instanceNumber')}
                id="instance-instanceNumber"
                name="instanceNumber"
                data-cy="instanceNumber"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.sopClassUid')}
                id="instance-sopClassUid"
                name="sopClassUid"
                data-cy="sopClassUid"
                type="text"
                validate={{
                  maxLength: { value: 100, message: translate('entity.validation.maxlength', { max: 100 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.patientOrientation')}
                id="instance-patientOrientation"
                name="patientOrientation"
                data-cy="patientOrientation"
                type="text"
                validate={{
                  maxLength: { value: 100, message: translate('entity.validation.maxlength', { max: 100 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.mediaStorageSopInstanceUid')}
                id="instance-mediaStorageSopInstanceUid"
                name="mediaStorageSopInstanceUid"
                data-cy="mediaStorageSopInstanceUid"
                type="text"
                validate={{
                  maxLength: { value: 100, message: translate('entity.validation.maxlength', { max: 100 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.transferSyntaxUid')}
                id="instance-transferSyntaxUid"
                name="transferSyntaxUid"
                data-cy="transferSyntaxUid"
                type="text"
                validate={{
                  maxLength: { value: 100, message: translate('entity.validation.maxlength', { max: 100 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.acquisitionDateTime')}
                id="instance-acquisitionDateTime"
                name="acquisitionDateTime"
                data-cy="acquisitionDateTime"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.imageType')}
                id="instance-imageType"
                name="imageType"
                data-cy="imageType"
                type="text"
                validate={{
                  maxLength: { value: 40, message: translate('entity.validation.maxlength', { max: 40 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.pixelSpacing')}
                id="instance-pixelSpacing"
                name="pixelSpacing"
                data-cy="pixelSpacing"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.imageOrientation')}
                id="instance-imageOrientation"
                name="imageOrientation"
                data-cy="imageOrientation"
                type="text"
                validate={{
                  maxLength: { value: 40, message: translate('entity.validation.maxlength', { max: 40 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.imagePosition')}
                id="instance-imagePosition"
                name="imagePosition"
                data-cy="imagePosition"
                type="text"
                validate={{
                  maxLength: { value: 80, message: translate('entity.validation.maxlength', { max: 80 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.sliceThickness')}
                id="instance-sliceThickness"
                name="sliceThickness"
                data-cy="sliceThickness"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.sliceLocation')}
                id="instance-sliceLocation"
                name="sliceLocation"
                data-cy="sliceLocation"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.windowCenter')}
                id="instance-windowCenter"
                name="windowCenter"
                data-cy="windowCenter"
                type="text"
                validate={{
                  maxLength: { value: 40, message: translate('entity.validation.maxlength', { max: 40 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.windowWidth')}
                id="instance-windowWidth"
                name="windowWidth"
                data-cy="windowWidth"
                type="text"
                validate={{
                  maxLength: { value: 40, message: translate('entity.validation.maxlength', { max: 40 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.xrayTubeCurrent')}
                id="instance-xrayTubeCurrent"
                name="xrayTubeCurrent"
                data-cy="xrayTubeCurrent"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.exposureTime')}
                id="instance-exposureTime"
                name="exposureTime"
                data-cy="exposureTime"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.kvp')}
                id="instance-kvp"
                name="kvp"
                data-cy="kvp"
                type="text"
                validate={{
                  maxLength: { value: 40, message: translate('entity.validation.maxlength', { max: 40 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.contentDateTime')}
                id="instance-contentDateTime"
                name="contentDateTime"
                data-cy="contentDateTime"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.columns')}
                id="instance-columns"
                name="columns"
                data-cy="columns"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.rows')}
                id="instance-rows"
                name="rows"
                data-cy="rows"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.photometricInterpretation')}
                id="instance-photometricInterpretation"
                name="photometricInterpretation"
                data-cy="photometricInterpretation"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.bitsAllocated')}
                id="instance-bitsAllocated"
                name="bitsAllocated"
                data-cy="bitsAllocated"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.bitsStored')}
                id="instance-bitsStored"
                name="bitsStored"
                data-cy="bitsStored"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.pixelRepresentation')}
                id="instance-pixelRepresentation"
                name="pixelRepresentation"
                data-cy="pixelRepresentation"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.highBit')}
                id="instance-highBit"
                name="highBit"
                data-cy="highBit"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.imageOrientationPatient')}
                id="instance-imageOrientationPatient"
                name="imageOrientationPatient"
                data-cy="imageOrientationPatient"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.imagePositionPatient')}
                id="instance-imagePositionPatient"
                name="imagePositionPatient"
                data-cy="imagePositionPatient"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.frameOfReferenceUid')}
                id="instance-frameOfReferenceUid"
                name="frameOfReferenceUid"
                data-cy="frameOfReferenceUid"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.microSec')}
                id="instance-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.timestampentry')}
                id="instance-timestampentry"
                name="timestampentry"
                data-cy="timestampentry"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.lastupdated')}
                id="instance-lastupdated"
                name="lastupdated"
                data-cy="lastupdated"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.cursor')}
                id="instance-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.active')}
                id="instance-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.instance.imageObservation')}
                id="instance-imageObservation"
                name="imageObservation"
                data-cy="imageObservation"
                type="text"
                validate={{
                  maxLength: { value: 500, message: translate('entity.validation.maxlength', { max: 500 }) },
                }}
              />
              <ValidatedField
                id="instance-series"
                name="series"
                data-cy="series"
                label={translate('tinyMedicRestApp.instance.series')}
                type="select"
                required
              >
                <option value="" key="0" />
                {series
                  ? series.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/instance" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default InstanceUpdate;
